import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { AutoComplete } from "primereact/autocomplete";
import { MultiSelect } from "primereact/multiselect";
import { toast } from "react-toastify";
import { createPurchaseInvoice, editPurchaseInvoice, fetchClientPackagesForPurchase } from "../../services/purchaseInvoiceService";
import { formatMoney, searchClients } from "../../services/clientPaymentService";
import styles from "./PurchaseInvoiceDialog.module.scss";

const blankItem = () => ({ description: "", url: "", quantity: 1, unitPrice: null });

// Create (no `invoice` prop) or edit (`invoice` = a purchase invoice) a
// purchase invoice - see POST /purchaseInvoice and PUT /purchaseInvoice/:id.
// Linking a package that has a flight number moves the invoice out of the
// Purchases tab and into that flight's normal group on the billing pages.
const PurchaseInvoiceDialog = ({ invoice, onHideDialog, onsuccess }) => {
    const dispatch = useDispatch();
    const isEdit = !!invoice;

    const [client, setClient] = useState(null);
    const [clientQuery, setClientQuery] = useState("");
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [merchant, setMerchant] = useState(invoice?.purchase?.merchant || "");
    const [orderNumber, setOrderNumber] = useState(invoice?.purchase?.orderNumber || "");
    const [items, setItems] = useState(invoice?.purchase?.items?.length ? invoice.purchase.items.map((i) => ({ ...i })) : [blankItem()]);
    const [shipping, setShipping] = useState(invoice?.purchase?.shipping || null);
    const [tax, setTax] = useState(invoice?.purchase?.tax || null);
    const [notes, setNotes] = useState(invoice?.notes || "");
    const [packageOptions, setPackageOptions] = useState([]);
    const [packageIds, setPackageIds] = useState((invoice?.purchase?.packages || []).map((p) => p.package));
    const [submitting, setSubmitting] = useState(false);

    // The detail page loads the invoice with clientId populated into an object.
    const clientId = isEdit ? invoice.clientId?._id || invoice.clientId : client?._id;

    useEffect(() => {
        if (!clientId) {
            setPackageOptions([]);
            return;
        }
        let cancelled = false;
        (async () => {
            const res = await fetchClientPackagesForPurchase(clientId);
            if (cancelled) return;
            setPackageOptions(
                (res?.data || []).map((p) => ({
                    value: p._id,
                    label: `${p.Waybill}${p.flightNumber ? ` - ${p.flightNumber}` : " - no flight yet"}${p.description ? ` - ${p.description}` : ""}`,
                })),
            );
        })();
        return () => {
            cancelled = true;
        };
    }, [clientId]);

    const searchClient = async (e) => {
        const q = (e.query || "").trim();
        if (q.length < 2) {
            setClientSuggestions([]);
            return;
        }
        const res = await searchClients(q);
        setClientSuggestions((res?.data || []).map((c) => ({ ...c, displayName: `${c.firstName} ${c.lastName} - ${c.email}` })));
    };

    const updateItem = (index, field, value) => setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
    const itemsTotal = items.reduce((sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.unitPrice) || 0), 0);
    const total = itemsTotal + (Number(shipping) || 0) + (Number(tax) || 0);
    const isValid = !!clientId && items.length > 0 && items.every((i) => i.description.trim() && Number(i.quantity) > 0 && Number(i.unitPrice) > 0);

    const handleSubmit = async () => {
        if (!isValid) {
            toast.warn("Pick a client and give every item a description, quantity and unit price above $0.");
            return;
        }
        setSubmitting(true);
        const cleanItems = items.map((i) => ({ description: i.description.trim(), url: (i.url || "").trim(), quantity: Number(i.quantity), unitPrice: Number(i.unitPrice) }));
        const res = isEdit ? await editPurchaseInvoice(invoice._id, { merchant, orderNumber, items: cleanItems, shipping: Number(shipping) || 0, tax: Number(tax) || 0, notes, packageIds }) : await createPurchaseInvoice(dispatch, { clientId, merchant, orderNumber, items: cleanItems, shipping: Number(shipping) || 0, tax: Number(tax) || 0, notes, packageIds });
        setSubmitting(false);
        if (res?.success) {
            if (isEdit) toast.success("Purchase invoice updated");
            if (onsuccess) onsuccess(res.data);
        }
    };

    return (
        <div className={styles.dialog}>
            <p className={styles.intro}>Goods bought for the client (Amazon, etc.), billed separately from shipping. Link it to a package with a flight number and it moves into that flight's normal invoice list.</p>

            <div className={styles.field}>
                <label>Client</label>
                {isEdit ? (
                    <InputText value={invoice.clientName || ""} disabled />
                ) : (
                    <AutoComplete
                        value={clientQuery}
                        suggestions={clientSuggestions}
                        completeMethod={searchClient}
                        field="displayName"
                        onChange={(e) => {
                            setClientQuery(typeof e.value === "string" ? e.value : e.value.displayName);
                            if (typeof e.value === "string") setClient(null);
                        }}
                        onSelect={(e) => {
                            setClient(e.value);
                            setPackageIds([]);
                        }}
                        placeholder="Start typing a client's name or email..."
                        style={{ width: "100%" }}
                        inputStyle={{ width: "100%" }}
                    />
                )}
            </div>

            <div className={styles.twoCol}>
                <div className={styles.field}>
                    <label>Merchant</label>
                    <InputText value={merchant} onChange={(e) => setMerchant(e.target.value)} placeholder="e.g. Amazon" />
                </div>
                <div className={styles.field}>
                    <label>Order Number</label>
                    <InputText value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
                </div>
            </div>

            <p className={styles.sectionTitle}>Items</p>
            <div className={styles.itemList}>
                {items.map((item, index) => (
                    <div className={styles.itemCard} key={index}>
                        <div className={styles.field}>
                            <label>Item</label>
                            <InputText value={item.description} onChange={(e) => updateItem(index, "description", e.target.value)} placeholder="What was bought" />
                        </div>
                        <div className={styles.field}>
                            <label>Link (optional)</label>
                            <InputText value={item.url} onChange={(e) => updateItem(index, "url", e.target.value)} placeholder="https://" />
                        </div>
                        <div className={styles.itemBottom}>
                            <div className={styles.field}>
                                <label>Qty</label>
                                <InputNumber value={item.quantity} onValueChange={(e) => updateItem(index, "quantity", e.value)} min={1} />
                            </div>
                            <div className={styles.field}>
                                <label>Unit Price</label>
                                <InputNumber value={item.unitPrice} onValueChange={(e) => updateItem(index, "unitPrice", e.value)} mode="currency" currency="USD" locale="en-US" placeholder="$0.00" />
                            </div>
                            <div className={styles.lineTotal}>
                                <span>Line Total</span>
                                <strong>{formatMoney((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}</strong>
                            </div>
                            <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))} disabled={items.length === 1} aria-label="Remove item" />
                        </div>
                    </div>
                ))}
            </div>

            <Button label="Add Item" icon="pi pi-plus" outlined onClick={() => setItems((prev) => [...prev, blankItem()])} className={styles.addBtn} />

            <div className={styles.twoCol}>
                <div className={styles.field}>
                    <label>Merchant shipping (optional)</label>
                    <InputNumber value={shipping} onValueChange={(e) => setShipping(e.value)} mode="currency" currency="USD" locale="en-US" min={0} placeholder="$0.00" />
                    <small className={styles.hint}>What the merchant charged to deliver the goods to us.</small>
                </div>
                <div className={styles.field}>
                    <label>Tax (optional)</label>
                    <InputNumber value={tax} onValueChange={(e) => setTax(e.value)} mode="currency" currency="USD" locale="en-US" min={0} placeholder="$0.00" />
                    <small className={styles.hint}>Sales tax the merchant charged on the goods.</small>
                </div>
            </div>

            <div className={styles.field}>
                <label>Linked packages (optional)</label>
                <MultiSelect value={packageIds} options={packageOptions} onChange={(e) => setPackageIds(e.value)} placeholder={clientId ? "Select the client's packages" : "Pick a client first"} disabled={!clientId} filter display="chip" emptyMessage="No packages for this client" />
            </div>

            <div className={styles.field}>
                <label>Notes</label>
                <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </div>

            <div className={styles.totalRow}>
                <span>Total</span>
                <strong>{formatMoney(total)}</strong>
            </div>

            <div className={styles.actions}>
                <Button label="Cancel" className="p-button-text" onClick={onHideDialog} disabled={submitting} />
                <Button label={submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Purchase Invoice"} icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"} onClick={handleSubmit} disabled={submitting || !isValid} />
            </div>
        </div>
    );
};

export default PurchaseInvoiceDialog;
